import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { Icon } from './icons';
import FloatingParticles from './FloatingParticles';
import UserRoleContext from '../context/UserRoleContext';
import type { UserRoleCtx } from '../context/UserRoleContext';
import type { UserAccessStatus } from '../types';

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

type GateState = 'loading' | 'requesting' | UserAccessStatus | 'unavailable';

interface Props {
  children: React.ReactNode;
}

export default function AccessGate({ children }: Props) {
  const { instance }      = useMsal();
  const cfg               = useRuntimeConfig();
  const [state, setState] = useState<GateState>('loading');
  const [ctx, setCtx]     = useState<UserRoleCtx>({
    role: null, status: null, isAdmin: false, userId: null, email: null, name: null,
  });

  function load() {
    if (DEV_BYPASS) {
      setCtx({ role: 'admin', status: 'approved', isAdmin: true, userId: 'dev', email: 'dev@fintinc.com', name: 'Dev User' });
      setState('approved');
      return;
    }

    setState('loading');
    const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    api.getMe()
      .then(me => {
        setCtx({
          role:    me.role,
          status:  me.status,
          isAdmin: me.role === 'admin',
          userId:  me.userId,
          email:   me.email,
          name:    me.name,
        });
        setState(me.status === 'not_registered' ? 'not_registered' : me.status);
      })
      .catch(err => {
        const msg = String((err as Error)?.message ?? '');
        if (msg.includes('401') || msg.includes('403')) {
          setState('rejected');
        } else {
          setState('unavailable');
        }
      });
  }

  function handleRequestAccess() {
    setState('requesting');
    const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    api.requestAccess()
      .then(me => {
        setCtx({
          role:    me.role,
          status:  me.status,
          isAdmin: me.role === 'admin',
          userId:  me.userId,
          email:   me.email,
          name:    me.name,
        });
        setState(me.status);
      })
      .catch(() => setState('unavailable'));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (state === 'loading') {
    return <div className="loading-screen"><span className="spinner" />Checking access…</div>;
  }

  if (state === 'not_registered') {
    return (
      <div className="login-screen">
        <FloatingParticles count={12} rise={620} />
        <div className="login-card">
          <div className="login-logo-wrap">
            <span className="login-mark"><Icon name="sparkle" size={22} /></span>
            <div className="login-wordmark">
              <span className="login-brand">FINT</span><span className="login-product">QualiForge</span>
            </div>
            <div className="login-tagline">FINT · AI-Powered Test Case Engineering</div>
          </div>
          <div className="login-icon-square accent"><Icon name="wave" size={24} /></div>
          <h2 className="login-title">Request access</h2>
          <p className="login-subtitle">
            You're signed in but don't have access to this portal yet.
            Click below to send a request to your organisation administrator.
          </p>
          <button
            className="btn btn-primary login-btn mt-md"
            onClick={handleRequestAccess}
          >
            Request access
          </button>
        </div>
      </div>
    );
  }

  if (state === 'requesting') {
    return <div className="loading-screen"><span className="spinner" />Sending request…</div>;
  }

  if (state === 'pending') {
    return (
      <div className="login-screen">
        <FloatingParticles count={12} rise={620} />
        <div className="login-card">
          <div className="login-logo-wrap">
            <span className="login-mark"><Icon name="sparkle" size={22} /></span>
            <div className="login-wordmark">
              <span className="login-brand">FINT</span><span className="login-product">QualiForge</span>
            </div>
            <div className="login-tagline">FINT · AI-Powered Test Case Engineering</div>
          </div>
          <div className="login-icon-square warn"><Icon name="hourglass" size={24} /></div>
          <h2 className="login-title">Request submitted</h2>
          <p className="login-subtitle">
            Your access request has been sent. An administrator will review it shortly.
            You'll be able to sign in once they approve your account.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'rejected') {
    return (
      <div className="login-screen">
        <FloatingParticles count={12} rise={620} />
        <div className="login-card">
          <div className="login-logo-wrap">
            <span className="login-mark"><Icon name="sparkle" size={22} /></span>
            <div className="login-wordmark">
              <span className="login-brand">FINT</span><span className="login-product">QualiForge</span>
            </div>
            <div className="login-tagline">FINT · AI-Powered Test Case Engineering</div>
          </div>
          <div className="login-icon-square danger"><Icon name="lock" size={24} /></div>
          <h2 className="login-title">Access denied</h2>
          <p className="login-subtitle">
            Your access to this portal has been rejected by an administrator.
            If you believe this is a mistake, contact your organisation administrator.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="login-screen">
        <FloatingParticles count={12} rise={620} />
        <div className="login-card">
          <div className="login-logo-wrap">
            <span className="login-mark"><Icon name="sparkle" size={22} /></span>
            <div className="login-wordmark">
              <span className="login-brand">FINT</span><span className="login-product">QualiForge</span>
            </div>
            <div className="login-tagline">FINT · AI-Powered Test Case Engineering</div>
          </div>
          <div className="login-icon-square warn"><Icon name="alert-triangle" size={24} /></div>
          <h2 className="login-title">Service unavailable</h2>
          <p className="login-subtitle">
            Could not reach the access-check service. The backend may still be deploying.
          </p>
          <button className="btn btn-primary login-btn mt-md" onClick={load}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <UserRoleContext.Provider value={ctx}>
      {children}
    </UserRoleContext.Provider>
  );
}
